import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CFormLabel, CFormInput, CFormTextarea, CFormSelect,
} from '@coreui/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import AxiosInstance from 'src/components/Axios';
import { useSupportChannels } from 'src/hooks/useSupportChannels';
import { useResponderTeams } from 'src/hooks/useResponderTeams';
import { useSupportCategories } from 'src/hooks/useSupportCategories';
import { invalidateLettersList, invalidateCachedSupportHistory } from 'src/utils/supportRequestCache';
import { isValidEmailInput, formatPhoneInput, isValidPhoneInput, phoneForApi, PHONE_PREFIX } from 'src/utils/inputValidation';
import { isValidEmailFormat } from 'src/utils/email';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { STATUS_LETTER_OPTIONS, SENDER_TYPE_OPTIONS, DEFAULT_CHANNEL_NAME } from 'src/constants/supportLetters';

const SupportLettersCreate = () => {
	const navigate = useNavigate();
	const channels = useSupportChannels();
	const allTeams = useResponderTeams();
	const allCategories = useSupportCategories();

	const [email, setEmail] = useState('');
	const [name, setName] = useState('');
	const [phone, setPhone] = useState(PHONE_PREFIX);
	const [channelId, setChannelId] = useState('');
	const [subject, setSubject] = useState('');
	const [sentAt, setSentAt] = useState('');
	const [status, setStatus] = useState('new');
	const [senderType, setSenderType] = useState('user');
	const [message, setMessage] = useState('');
	const [selectedTeams, setSelectedTeams] = useState([]);
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState({});

	const teamOptions = allTeams.map(t => ({ value: t.id, label: `${t.name} (${t.code})` }));

	const catOptions = allCategories
		.filter(c => c.scopes?.some(s => s === 'conversation' || s === 'all'))
		.map(c => ({ value: c.id, label: c.name }));

	// Канал за замовчуванням — "Пошта підтримки КомКом" (можна змінити вручну)
	useEffect(() => {
		if (channelId || channels.length === 0) return;
		const defaultChannel = channels.find(ch => ch.name === DEFAULT_CHANNEL_NAME);
		if (defaultChannel) setChannelId(String(defaultChannel.id));
	}, [channels, channelId]);

	const handleSubmit = async () => {
		const newErrors = {
			email: email.trim() !== '' && !isValidEmailFormat(email),
			name: !name.trim(),
			phone: !isValidPhoneInput(phone),
			channelId: !channelId,
			subject: !subject.trim(),
			sentAt: !sentAt,
			message: !message.trim(),
		};
		if (Object.values(newErrors).some(Boolean)) {
			setErrors(newErrors);
			const missing = [
				newErrors.email && 'Email (невірний формат)',
				newErrors.name && "Ім'я",
				newErrors.phone && 'Телефон (невірний формат)',
				newErrors.channelId && 'Канал',
				newErrors.subject && 'Тема',
				newErrors.sentAt && 'Дата отримання',
				newErrors.message && 'Повідомлення',
			].filter(Boolean);
			toast.warning(`Не заповнено: ${missing.join(', ')}`);
			return;
		}
		setErrors({});
		setSubmitting(true);
		const params = {
			user_email: email.trim(),
			user_name: name.trim(),
			user_phone: phoneForApi(phone),
			channel_id: parseInt(channelId),
			subject: subject.trim(),
			sent_at: sentAt,
			status,
			message: message.trim(),
			sender_type: senderType,
			parent_id: null,
			responder_team_ids: selectedTeams.map(t => t.value),
			category_ids: selectedCategories.map(c => c.value),
		};
		// console.log('params', params);
		// setTimeout(() => {
		// 	setSubmitting(false);
		// }, 2000);
		try {
			const res = await AxiosInstance.post('/support-requests', params);
			invalidateLettersList();
			invalidateCachedSupportHistory(email.trim());
			navigate(`/support-letters/view/${res.data.id}`, { replace: true });
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка створення');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<ToastContainer />
			<CCard className="mb-4">
				<CCardHeader><strong>Новий запит підтримки</strong></CCardHeader>
				<CCardBody>
					<FormSubmitOverlay submitting={submitting}>
						<CRow className="g-3">
							<CCol md={4}>
								<CFormLabel>Відправник</CFormLabel>
								<CFormSelect value={senderType} onChange={e => setSenderType(e.target.value)}>
									{SENDER_TYPE_OPTIONS.map(o => (
										<option key={o.value} value={o.value}>{o.label}</option>
									))}
								</CFormSelect>
							</CCol>
							<CCol md={4}>
								<CFormLabel>Email</CFormLabel>
								<CFormInput
									value={email}
									onChange={e => {
										const val = e.target.value;
										if (isValidEmailInput(val)) {
											setEmail(val);
											if (errors.email) setErrors(p => ({ ...p, email: false }));
										}
									}}
									invalid={!!errors.email}
									autoComplete="off"
								/>
							</CCol>
							<CCol md={4}>
								<CFormLabel>Ім'я *</CFormLabel>
								<CFormInput
									value={name}
									onChange={e => { setName(e.target.value); if (errors.name) setErrors(p => ({ ...p, name: false })); }}
									invalid={!!errors.name}
									autoComplete="off"
								/>
							</CCol>
							<CCol md={4}>
								<CFormLabel>Телефон</CFormLabel>
								<CFormInput
									value={phone}
									onChange={e => {
										const formatted = formatPhoneInput(e.target.value);
										if (formatted !== null) {
											setPhone(formatted);
											if (errors.phone) setErrors(p => ({ ...p, phone: false }));
										}
									}}
									invalid={!!errors.phone}
									autoComplete="off"
								/>
							</CCol>
							<CCol md={4}>
								<CFormLabel>Дата і час отримання *</CFormLabel>
								<CFormInput
									type="datetime-local"
									value={sentAt}
									onChange={e => { setSentAt(e.target.value); if (errors.sentAt) setErrors(p => ({ ...p, sentAt: false })); }}
									invalid={!!errors.sentAt}
								/>
							</CCol>
							<CCol md={4}>
								<CFormLabel>Статус</CFormLabel>
								<CFormSelect value={status} onChange={e => setStatus(e.target.value)}>
									{STATUS_LETTER_OPTIONS.map(o => (
										<option key={o.value} value={o.value}>{o.label}</option>
									))}
								</CFormSelect>
							</CCol>
							<CCol md={4}>
								<CFormLabel>Канал *</CFormLabel>
								<CFormSelect
									value={channelId}
									onChange={e => { setChannelId(e.target.value); if (errors.channelId) setErrors(p => ({ ...p, channelId: false })); }}
									invalid={!!errors.channelId}
								>
									<option value="">— оберіть канал —</option>
									{channels.map(ch => (
										<option key={ch.id} value={ch.id}>{ch.name}</option>
									))}
								</CFormSelect>
							</CCol>
							<CCol xs={8}>
								<CFormLabel>Команди відповідальних</CFormLabel>
								<Select
									isMulti
									options={teamOptions}
									value={selectedTeams}
									onChange={setSelectedTeams}
									placeholder="Оберіть команди..."
									isClearable
									classNamePrefix="react-select"
								/>
							</CCol>
							<CCol xs={12}>
								<CFormLabel>Категорії сесії</CFormLabel>
								<Select
									isMulti
									options={catOptions}
									value={selectedCategories}
									onChange={setSelectedCategories}
									placeholder="Оберіть категорії..."
									isClearable
									classNamePrefix="react-select"
								/>
							</CCol>
							<CCol md={12}>
								<CFormLabel>Тема *</CFormLabel>
								<CFormInput
									value={subject}
									onChange={e => { setSubject(e.target.value); if (errors.subject) setErrors(p => ({ ...p, subject: false })); }}
									invalid={!!errors.subject}
									autoComplete="off"
								/>
							</CCol>
							<CCol xs={12}>
								<CFormLabel>Повідомлення *</CFormLabel>
								<CFormTextarea
									rows={6}
									value={message}
									onChange={e => { setMessage(e.target.value); if (errors.message) setErrors(p => ({ ...p, message: false })); }}
									invalid={!!errors.message}
									placeholder="Текст повідомлення від клієнта..."
								/>
							</CCol>
						</CRow>
						<div className="d-flex gap-2 mt-4">
							<CButton color="secondary" variant="outline" onClick={() => navigate(-1)} title="Повернутись до попередньої сторінки">
								← Назад
							</CButton>
							<CButton color="success" onClick={handleSubmit} disabled={submitting} title="Створити новий запит підтримки">
								{submitting ? 'Створення...' : 'Створити'}
							</CButton>
						</div>
					</FormSubmitOverlay>
				</CCardBody>
			</CCard>
		</>
	);
};

export default SupportLettersCreate;
